import StackKit from "@/shared/components/kit/stack";
import { useState } from "react";
import TableWallet from "../../walletReports/table";
import CardBoxLayout from "@/modules/user/layout/cardBoxLayout";
import { useQuery } from "@tanstack/react-query";
import { fetchCreditPurchases } from "@/api/user/home";
import {numberSeparator, toPersianNumber,} from "@/hooks/useThousandsSeparator";
import NorthWestIcon from "@mui/icons-material/NorthWest";
import moment from "jalali-moment";
import { useNavigate } from "react-router";
import ContentSkeleton from "../../walletReports/content.skeleton";

const Content = () => {
  const [loanID] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "storePoint-data",
      loanID,
      currentPage,
      itemsPerPage,
    ],
    queryFn: () => fetchCreditPurchases(loanID, currentPage),
  });

  const paginationData = data?.data
      ? {
        current_page: data.data.current_page || currentPage,
        per_page: data.data.per_page || itemsPerPage,
        total: data.data.total || 0,
      }
      : undefined;

  const dataHeader = [
    { id: "amount", title: "خرید اعتباری" },
    { id: "installmentsCount", title: "کل اقساط" },
    { id: "payedCount", title: "اقساط پرداخت شده" },
    { id: "totalPayed", title: "مبلغ هر قسط" },
    { id: "clearingAmount", title: "مبلغ تسویه یکجا" },
    { id: "start", title: "تاریخ دریافت اعتبار" },
    { id: "end", title: "تاریخ پایان اعتبار" },
    { id: "details", title: "پذیرنده دریافت کننده اعتبار" },
    { id: "status", title: "وضعیت" },
    { id: "detailButton", title: "" },
  ];

  if (isLoading) {
    return (
        <CardBoxLayout>
          <ContentSkeleton />
        </CardBoxLayout>
    );
  }

  if (isError) {
    return (
        <CardBoxLayout>
          <StackKit
              justifyContent="center"
              alignItems="center"
              sx={{ py: 4 }}
          >
            <p>خطا در دریافت اطلاعات</p>
          </StackKit>
        </CardBoxLayout>
    );
  }

  const list = Array.isArray(data?.data?.loans)
      ? data.data.loans
      : Array.isArray(data?.data)
          ? data.data
          : [];

  const newDataStorePont = list.map((item: any, index: number) => {
    const installments = Array.isArray(item?.data?.installments)
        ? item.data.installments
        : [];

    const rowId =
        (paginationData?.current_page - 1) *
        (paginationData?.per_page || 10) +
        index +
        1;

    const status =
        item?.status === "open" ? (
            <div className="px-2 py-1 rounded-full bg-yellow-500/25 text-yellow-500 border-1 border-yellow-500">
              باز
            </div>
        ) : item?.status === "payed" ? (
            <div className="px-2 py-1 rounded-full bg-green-500/25 text-green-500 border-1 border-green-500">
              پرداخت شده
            </div>
        ) : (
            <div className="px-2 py-1 rounded-full bg-red-500/25 text-red-500 border-1 border-red-500">
              لغو شده
            </div>
        );

    return {
      rowId: toPersianNumber(rowId),

      ...item,

      totalPayed: numberSeparator(
          `${item?.data?.totalPayed ?? 0}`
      ),

      installmentsCount: toPersianNumber(
          item?.data?.installmentsCount ?? 0
      ),

      payedCount: toPersianNumber(
          item?.data?.payedCount ?? 0
      ),

      amount: toPersianNumber(
          numberSeparator(Number(item?.amount ?? 0))
      ),

      sumPayment: numberSeparator(
          `${installments[0]?.sum ?? 0}`
      ),

      clearingAmount: numberSeparator(
          item?.data?.clearingAmount ?? 0
      ),

      status,

      start: toPersianNumber(
          moment
              .unix(item?.start ?? 0)
              .locale("fa")
              .format("YYYY-MM-DD")
      ),

      end: toPersianNumber(
          moment
              .unix(item?.end ?? 0)
              .locale("fa")
              .format("YYYY-MM-DD")
      ),

      details: item?.details ?? "",

      detailButton: (
          <StackKit
              justifyContent="center"
              alignItems="center"
              sx={{
                background: "linear-gradient(147.72deg, #272727 -17.1%, #333333 118.66%)",
                borderRadius: "50%",
                padding:'8px',
                cursor: "pointer",
              }}
              onClick={() => navigate(`/credits/${item?.id}`)}
          >
            <NorthWestIcon
                style={{
                  color: "#fff",
                  width: "15px",
                }}
            />
          </StackKit>
      ),
    };
  });

  if (list.length === 0) {
    return (
        <CardBoxLayout>
          <StackKit
              justifyContent="center"
              alignItems="center"
              sx={{ py: 4 }}
          >
            <p>اطلاعاتی برای نمایش وجود ندارد</p>
          </StackKit>
        </CardBoxLayout>
    );
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
      <CardBoxLayout>
        <StackKit
            alignItems="start"
            justifyContent="start"
        >
          <TableWallet
              dataHeader={dataHeader}
              dataTbody={newDataStorePont}
              pagination={paginationData}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
          />
        </StackKit>
      </CardBoxLayout>
  );
};

export default Content;